import React from 'react'
import ReactHtmlParser from 'react-html-parser'
import { Button, Modal, Form, Row, Col } from 'react-bootstrap'
import { toast } from 'react-toastify'
import styled from '@emotion/styled'
import { css } from '@emotion/react'
import { useForm, FormProvider } from 'react-hook-form'
import { joiResolver } from '@hookform/resolvers/joi'
import { DevTool } from '@hookform/devtools'
// import EducationArray from '@/components/SoleraNetwork/GreenhouseEducationArray'
import { defaultValues } from '@/components/SoleraNetwork/GreenhouseApplication'
import Checkbox from '@/components/SoleraNetwork/FormControls/Checkbox'
import LocationInput from '@/components/SoleraNetwork/FormControls/LocationInput'
import { jobPoster } from '@/libs/greenhouse'
import SelectOptions from '@/components/SoleraNetwork/FormControls/SelectOptions'
import SelectCheckboxes from '@/components/SoleraNetwork/FormControls/SelectCheckboxes'
import TextArea from '@/components/SoleraNetwork/FormControls/TextArea'
import FileUpload from '@/components/SoleraNetwork/FormControls/FileUpload'
import InputText from '@/components/SoleraNetwork/FormControls/InputText'
import InputHidden from '@/components/SoleraNetwork/FormControls/InputHidden'

export const RequiredIndicator = styled.span`
  color: red;
  margin-left: 5px;
`

export const Label = css`
  font-size: 14px !important;
  text-align: right;
  justify-content: flex-end;
`

const RowVCenter = css`
  align-items: flex-start;
  margin-bottom: 0.5em;
`

const GreenhouseApplicationForm = ({ schema, onClose, ghSrc, ...job }) => {
  const methods = useForm({
    resolver: joiResolver(schema),
    defaultValues: defaultValues(ghSrc),
  })
  const { register, control, handleSubmit, reset } = methods
  const jobQuestions = job.questions
  const locationQuestions = job.location_questions ? job.location_questions : []
  const compliance = job.compliance ? job.compliance : []
  const questions = [...jobQuestions, ...locationQuestions]

  const onToastClose = () => reset(defaultValues(ghSrc))

  const onFormSubmit = data => {
    // Reshape the data before sending it off
    delete data.resume
    if (data.cover_letter.length === 0) {
      delete data.cover_letter
    }
    jobPoster(job.id, data).then(data => {
      if (data.success === true) {
        toast('Your application has been received.', {
          position: 'bottom-right',
          type: 'success',
          onClose: onToastClose,
        })
      } else {
        console.error('Something went wrong', data)
        toast('Something went wrong', {
          position: 'bottom-right',
          type: 'error',
        })
      }
    })

    onClose() // close the modal after successful submit
  }

  return (
    <>
      <FormProvider {...methods}>
        <Form onSubmit={handleSubmit(onFormSubmit)}>
          <Modal.Body>
            {questions.map((question, index) => (
              <FieldLayout
                question={question}
                key={`fieldLayout-${index}`}
                register={register}
              />
            ))}
            {/* // 66805 - Temp removal until proper fix can be established that respects the config settings coming from Greenhouse admin portal
            <EducationArray
              disciplines={job.disciplines}
              degrees={job.degrees}
            />
            <hr /> */}
            {compliance.map((question, index) => (
              <React.Fragment key={`complianceQuestions-${index}`}>
                <div
                  key={`questionDesc-${index}`}
                  dangerouslySetInnerHTML={{
                    __html: ReactHtmlParser(question.description),
                  }}
                />
                {question.questions.map((q, i) => (
                  <React.Fragment key={`complianceQuestion-${q.label}-${i}`}>
                    <FieldLayout question={q} register={register} />
                    {q.label === 'Race' && (
                      <p>
                        <a
                          href="https://boards.cdn.greenhouse.io/docs/RaceEthnicityDefinitions.pdf"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Race &amp; Ethnicity Definitions
                        </a>
                      </p>
                    )}
                    {q.label === 'VeteranStatus' && (
                      <>
                        <hr />
                        <div className={Label}>
                          {/* TODO: Followup with Greenhouse on where to grab this */}
                          <p>
                            <span>Form CC-305</span>
                            <br />
                            <span>OMB Control Number 1250-0005</span>
                            <br />
                            <span>Expires 05/31/2023</span>
                          </p>
                        </div>
                        <h4 id="disability_status_header">
                          Voluntary Self-Identification of Disability
                        </h4>
                      </>
                    )}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
            <hr />
            <Checkbox
              name="data_compliance.gdpr_consent_given"
              label="Solera Network has my consent to collect, store, and process my
              data for the purpose of considering me for employment."
              value="1"
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
            <Button type="submit">Submit Application</Button>
          </Modal.Footer>
        </Form>
      </FormProvider>

      <DevTool control={control} />
    </>
  )
}

export default GreenhouseApplicationForm

const FieldLayout = ({ question, register }) => {
  const [field] = question.fields || question.questions
  const isHiddenField = field.type === 'input_hidden'

  if (isHiddenField) {
    return (
      <>
        {question.fields.map((field, index) => (
          <FormField
            key={`hiddenField-${index.toString()}`}
            field={field}
            requiredString={question.required}
            register={register}
          />
        ))}
      </>
    )
  }

  const labelReplacements = {
    Location: `${question.label} (City)`,
    VeteranStatus: 'Veteran Status',
    DisabilityStatus: 'Disability Status',
  }

  const labelText = labelReplacements[question.label] || question.label

  return (
    <Form.Group as={Row} css={RowVCenter}>
      <Form.Label column sm="3" css={Label}>
        <span>{labelText}</span>{' '}
        {question.required && <RequiredIndicator>*</RequiredIndicator>}
      </Form.Label>
      <Col sm="9">
        {question.description && (
          <div className="description">
            {ReactHtmlParser(question.description)}
          </div>
        )}
        {question.fields.map((field, index) => (
          <FormField
            key={`innerField-${index.toString()}`}
            field={field}
            requiredString={question.required}
            register={register}
          />
        ))}
      </Col>
    </Form.Group>
  )
}

// eslint-disable-next-line no-unused-vars
const FormField = ({ field, requiredString, skipTextAreaCheck }) => {
  const props = {
    name: field.name,
    values: field.values && field.values.length ? field.values : null,
  }

  if (
    !skipTextAreaCheck &&
    (props.name === 'resume_text' || props.name === 'cover_letter_text')
  ) {
    return null
  }

  const FIELD_TYPES = {
    input_hidden: <InputHidden {...props} />,
    input_text: <InputText {...props} />,
    input_file: <FileUpload {...props} />,
    textarea: <TextArea {...props} />,
    multi_value_single_select: <SelectOptions {...props} />,
    multi_value_multi_select: <SelectCheckboxes {...props} />,
  }

  if (field.name === 'location') {
    return (
      <LocationInput
        requestOptions={{
          types: ['(cities)'],
          componentRestrictions: { country: ['us'] },
        }}
        {...props}
      />
    )
  }

  if (field.type in FIELD_TYPES) {
    return FIELD_TYPES[field.type]
  }

  return null
}
